import { logout } from '@/services/auth';
import { clearStoredToken, getLoginPath, type AuthScope } from '@/services/authScope';
import { logoutSubAccount } from '@/services/subAuth';
import { LogoutOutlined } from '@ant-design/icons';
import { useEmotionCss } from '@ant-design/use-emotion-css';
import { history, useModel } from '@umijs/max';
import { App, Spin } from 'antd';
import { stringify } from 'querystring';
import type { MenuInfo } from 'rc-menu/lib/interface';
import React, { useCallback } from 'react';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const displayName = initialState?.currentUser?.name || initialState?.currentSubAccount?.name;

  return <span className={'anticon'}>{displayName}</span>;
};

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ children }) => {
  const { message } = App.useApp();

  const actionClassName = useEmotionCss(({ token }) => {
    return {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    };
  });

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick = useCallback(
    async (event: MenuInfo) => {
      const { key } = event;
      if (key !== 'logout') {
        return;
      }

      const scope =
        initialState?.currentAuthScope === 'sub' || initialState?.currentSubAccount
          ? ('sub' as Exclude<AuthScope, 'public'>)
          : ('admin' as Exclude<AuthScope, 'public'>);

      try {
        if (scope === 'sub') {
          await logoutSubAccount();
        } else {
          await logout();
        }
      } catch (_e) {
        // ignore
      }

      clearStoredToken(scope);
      setInitialState((state) => ({
        ...state,
        currentAuthScope: 'public',
        currentUser: scope === 'admin' ? undefined : state?.currentUser,
        currentSubAccount: scope === 'sub' ? undefined : state?.currentSubAccount,
      }));

      const { search, pathname } = window.location;
      const urlParams = new URL(window.location.href).searchParams;
      const redirect = urlParams.get('redirect');
      const loginPath = getLoginPath(scope);

      if (window.location.pathname !== loginPath && !redirect) {
        history.replace({
          pathname: loginPath,
          search: stringify({
            redirect: pathname + search,
          }),
        });
      }

      message.success('退出登录成功');
    },
    [initialState, message, setInitialState],
  );

  const loading = (
    <span className={actionClassName}>
      <Spin size={'small'} style={{ marginLeft: 8, marginRight: 8 }} />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const displayName = initialState.currentUser?.name || initialState.currentSubAccount?.name;

  if (!displayName) {
    return loading;
  }

  const menuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
